<template>
  <div>
    <b-row>
      <b-col>
        <b-row>
          <b-col>
            <b-form-group>
              <b-form-file
                v-model="file"
                :disabled="busy"
                :placeholder="trans('upload_video')"
                :drop-placeholder="trans('drop_file_here')"
                :browse-text="trans('browse')"
                accept="video/mp4, video/webm"
                @input="onVideoFileChange"
              />
            </b-form-group>
          </b-col>
          <b-col class="col-auto">
            <b-button :variant="show_media ? 'danger' : 'primary'" @click="show_media = !show_media">
              {{ trans(show_media ? 'cancel' : 'media') }}
            </b-button>
          </b-col>
        </b-row>
        <b-form-group v-if="false" :label="trans('video_link')">
          <b-form-input v-model="cta_url" placeholder="https://example.com" @input="$emit('image_cta_url', cta_url)"/>
        </b-form-group>
        <b-alert
          v-if="!organization.youtube_handle && !organization.vimeo_user_id"
          variant="primary"
          show
          v-html="trans('template_video_connect')"
        ></b-alert>
        <b-form-group v-if="!onlyVideo && type != 'video' || ['video-card', 'video-tile'].indexOf(type) >= 0"
                      :label="trans('size')">
          <b-form-radio-group v-model="percent" :options="percentOptions" @input="$emit('image_percent', percent)"/>
        </b-form-group>
        <b-form-group v-if="type == 'video-column'" :label="trans('button_text')">
          <b-form-input v-model="btn_txt" :placeholder="trans('button_text')"/>
        </b-form-group>
        <b-form-group v-if="showClear">
          <b-button variant="link" class="text-danger" @click="url = null; $emit('image_url', null)">
            <i class="fa fa-fw fa-times"></i>
            {{ trans('remove') }}
          </b-button>
        </b-form-group>
        <div v-if="vimeo && vimeo.am_can_gate">
          <b-form-checkbox switch v-model="vimeo_gated" :disabled="busy" @input="onVimeoChange">
            {{ trans('gate_this_video') }}
          </b-form-checkbox>
          <div v-if="vimeo_gated">
            <p>
              {{ trans('gate_this_video_about') }}
            </p>
            <b-row>
              <b-col>
                <b-form-group :label="`${trans('expiration')} (${trans('optional').toLowerCase()})`">
                  <datetime v-model="vimeo_gated_expired_at" :config="dateOptions" :disabled="busy"
                            @input="onVimeoChange"/>
                </b-form-group>
              </b-col>
              <b-col>
                <b-form-group :label="`${trans('max_view_count')} (${trans('optional').toLowerCase()})`">
                  <b-form-input type="number" v-model="vimeo_gated_max_view_count" :disabled="busy"
                                @input="onVimeoChange"/>
                </b-form-group>
              </b-col>
            </b-row>
            <p>
              <small class="text-muted">{{ vimeoGatedUrl }}</small>
            </p>
          </div>
        </div>
      </b-col>
      <b-col class="text-center align-self-center" v-if="!show_media">
        <p class="lead text-center" v-if="busy">
          <i class="fa fa-fw fa-circle-notch fa-spin"></i>
          <span v-if="upload_percent === null">{{ trans('please_wait') }}</span>
          <span v-if="upload_percent !== null && upload_percent < 100">
            {{ trans('uploading') }}
            {{ upload_percent }}%
          </span>
          <span v-if="upload_percent == 100">
            {{ trans('generating_preview_thumbnail') }}
          </span>
        </p>
        <b-img v-if="!busy" fluid :src="url || videoPlaceholderUrl"/>
      </b-col>
      <b-col v-if="show_media">
        <b-tabs pills>
          <b-tab :title="trans('your_videos')" lazy :active="isVideo">
            <videos-table
              :organization="organization"
              :per-page="3"
              hide-upload
              video-name-only
              no-open
              @choose="onChooseVideo"
              class="mt-2"
            ></videos-table>
          </b-tab>
          <b-tab v-if="organization.youtube_handle" title="Youtube" lazy :active="isYoutube">
            <third-party-videos-table
              :organization="organization"
              :rows="3"
              type="youtube"
              video-name-only
              hide-upload
              no-open
              @choose="onChooseYoutube"
              class="mt-2"
            ></third-party-videos-table>
          </b-tab>
          <b-tab v-if="organization.vimeo_user_id" title="Vimeo" lazy :active="isVimeo">
            <third-party-videos-table
              :organization="organization"
              :rows="3"
              type="vimeo"
              video-name-only
              hide-upload
              no-open
              @choose="onChooseVimeo"
              class="mt-2"
            ></third-party-videos-table>
          </b-tab>
        </b-tabs>
      </b-col>
    </b-row>
    <b-row v-if="type == 'video-column'">
      <b-col>
        <b-form-group :label="trans('text')">
          <ckeditor
            id="ckeditor"
            v-model="txt"
            :editor="editor"
            :config="editorConfig"
            @input="onTxtChange"
          />
        </b-form-group>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import VideosTable from './../media/VideosTable'
import ThirdPartyVideosTable from './../media/ThirdPartyVideosTable'
import Datetime from 'vue-bootstrap-datetimepicker'

import ClassicEditor from '@ckeditor/ckeditor5-editor-classic/src/classiceditor'
import Essentials from '@ckeditor/ckeditor5-essentials/src/essentials'
import Heading from '@ckeditor/ckeditor5-heading/src/heading'
import Bold from '@ckeditor/ckeditor5-basic-styles/src/bold'
import Underline from '@ckeditor/ckeditor5-basic-styles/src/underline'
import Italic from '@ckeditor/ckeditor5-basic-styles/src/italic'
import Font from '@ckeditor/ckeditor5-font/src/font'
import Link from '@ckeditor/ckeditor5-link/src/link'
import List from '@ckeditor/ckeditor5-list/src/list'
import Indent from '@ckeditor/ckeditor5-indent/src/indent'
import IndentBlock from '@ckeditor/ckeditor5-indent/src/indentblock'
import Alignment from '@ckeditor/ckeditor5-alignment/src/alignment'
import BlockQuote from '@ckeditor/ckeditor5-block-quote/src/blockquote'
import Placeholder from '@ckeditor/ckeditor5-placeholder/src/placeholder'
import CKEditor from '@ckeditor/ckeditor5-vue2'

export default {
  props: {
    organization: Object,
    type: {
      type: String,
      default: 'video',
    },
    image_url: String,
    image_alt: String,
    image_cta_url: String,
    image_percent: {
      type: Number,
      default: 0.5,
    },
    button_text: String,
    button_url: String,
    text: String,
    meta: Object,
    onlyVideo: {
      type: Boolean,
      default: false,
    },
    showClear: {
      type: Boolean,
      default: false,
    },
    percentOptions: {
      type: Array,
      default() {
        return [{
          text: '33%',
          value: 0.3333,
        }, {
          text: '50%',
          value: 0.5,
        }]
      }
    },
  },
  components: {
    Datetime,
    VideosTable,
    ThirdPartyVideosTable,
    Placeholder,
    ckeditor: CKEditor.component,
  },
  data() {
    return {
      trans: trans,
      busy: false,
      error: null,
      file: null,
      raw_video: null,
      upload_percent: null,
      url: this.image_url,
      alt: this.image_alt,
      cta_url: this.image_cta_url,
      percent: this.image_percent,
      txt: this.text,
      txtSetFromEmpty: false,
      btn_txt: this.button_text,
      btn_url: this.button_url,
      show_media: false,
      vimeo: !!this.meta && !!this.meta.vimeo_gated ? {
        id: this.meta.vimeo_gated.id,
        url: this.meta.vimeo_gated.url,
        am_can_gate: true,
        am_gated: true
      } : null,
      vimeo_gated: !!this.meta && !!this.meta.vimeo_gated,
      vimeo_gated_expired_at: !!this.meta && !!this.meta.vimeo_gated ? moment(this.meta.vimeo_gated.expired_at).format('LL') : null,
      vimeo_gated_max_view_count: !!this.meta && !!this.meta.vimeo_gated ? this.meta.vimeo_gated.max_view_count : null,
      dateOptions: {
        locale: window.lang,
        format: 'LL',
        showClear: true,
      },
      // ckeditor
      editor: ClassicEditor,
      editorConfig: {
        language: window.lang,
        plugins: [
          Heading,
          Essentials,
          Bold,
          Italic,
          Underline,
          Font,
          Link,
          List,
          Indent,
          IndentBlock,
          Alignment,
          BlockQuote,
          Placeholder,
        ],
        toolbar: [
          'heading',
          'placeholder',
          'bold',
          'italic',
          'underline',
          'fontFamily',
          'fontSize',
          'fontColor',
          'link',
          'blockQuote',
          '|',
          'alignment',
          'bulletedList',
          'numberedList',
          'indent',
          'outdent',
          '|',
          'undo',
          'redo',
        ],
        fontSize: _.ckeditorFontSize,
        fontColor: {
          colors: _.ckeditorColors(this.organization),
          columns: 5
        },
        placeholderProps: {
          types: _.getPlaceholders(),
        },
      },
    }
  },
  computed: {
    videoPlaceholderUrl() {
      return '/img/frontend/blocks/video.png';
    },
    isVideo() {
      return !this.vimeo && (this.cta_url || '').indexOf('https://am.lol') === 0
    },
    isYoutube() {
      return !this.vimeo && _.getYouTubeVideoId(this.cta_url) != null
    },
    isVimeo() {
      return !!this.vimeo || _.getVimeoVideoId(this.cta_url) != null
    },
    metaForVimeo() {
      let meta = null
      if (this.vimeo && this.vimeo_gated) {
        meta = {
          vimeo_gated: {
            id: this.vimeo.id,
            url: this.vimeo.url,
            expired_at: this.vimeo_gated_expired_at ? moment(this.vimeo_gated_expired_at, 'LL').toISOString() : null,
            max_view_count: this.vimeo_gated_max_view_count,
          }
        }
      }
      return meta
    },
    vimeoGatedUrl() {
      if (!this.vimeo || !this.vimeo_gated || this.cta_url.indexOf('/org/') !== 0) return null
      return window.location.protocol + '//' + window.location.hostname + this.cta_url
    },
    source() {
      if (this.isVideo) return 'aws'
      if (this.isYoutube) return 'youtube'
      if (this.isVimeo) return 'vimeo'
      return null
    },
    video() {
      return {
        source: this.source,
        image_url: this.url,
        video_url: this.cta_url,
        meta: this.metaForVimeo,
        name: this.raw_video ? this.raw_video.name : null,
        description: this.raw_video ? this.raw_video.description : null,
        raw: this.raw_video,
      }
    },
  },
  watch: {
    txt() {
      this.$emit('text', this.txt)
    },
    btn_txt() {
      this.$emit('button_text', this.btn_txt)
    },
    button_text() {
      this.btn_txt = this.button_text
    },
    button_url() {
      this.btn_url = this.button_url
    },
    text() {
      this.txt = this.text
    },
    image_url() {
      this.url = this.image_url
    },
    image_alt() {
      this.alt = this.image_alt
    },
    image_cta_url() {
      this.cta_url = this.image_cta_url
    },
    image_percent() {
      this.percent = this.image_percent
    },
    meta() {
      this.vimeo = !!this.meta && !!this.meta.vimeo_gated ? {
        id: this.meta.vimeo_gated.id,
        url: this.meta.vimeo_gated.url,
        am_can_gate: true,
        am_gated: true
      } : null
      this.vimeo_gated = !!this.meta && !!this.meta.vimeo_gated
      this.vimeo_gated_expired_at = !!this.meta && !!this.meta.vimeo_gated ? moment(this.meta.vimeo_gated.expired_at).format('LL') : null
      this.vimeo_gated_max_view_count = !!this.meta && !!this.meta.vimeo_gated ? this.meta.vimeo_gated.max_view_count : null
    },
  },
  methods: {
    onVimeoChange() {
      if (this.vimeo && this.vimeo_gated) {
        this.cta_url = `/org/${this.organization.id}/vimeo/${this.vimeo.id}?placeholder`
        if (this.vimeo_gated_expired_at)
          this.cta_url += `&expired_at=${moment(this.vimeo_gated_expired_at, 'LL').format('YYYY-MM-DD')}`
        if (this.vimeo_gated_max_view_count)
          this.cta_url += `&max_view_count=${this.vimeo_gated_max_view_count}`
      } else {
        this.cta_url = this.vimeo.url
      }
      this.emitChanges()
    },
    emitChanges() {
      this.$emit('image_url', this.url)
      this.$emit('image_alt', this.alt)
      this.$emit('image_cta_url', this.cta_url)
      this.$emit('button_url', this.cta_url)
      this.$emit('meta', this.metaForVimeo)
      this.$emit('video', this.video)
    },
    setTxt(video) {
      if (!(this.txt || '').trim()) this.txtSetFromEmpty = true
      if (this.txtSetFromEmpty || this.onlyVideo) {
        this.txt = `<h5>${video.name}</h5><p>${video.description || ''}</p>`
      }
    },
    onTxtChange() {
      this.txtSetFromEmpty = !(this.txt || '').trim()
    },
    onChooseVideo(video) {
      this.raw_video = video
      this.vimeo = null
      this.url = video.thumb_url
      this.alt = video.name
      this.cta_url = video.short_url
      this.setTxt(video)
      this.btn_txt = trans('play_video')
      this.show_media = false
      this.generatePreview(this.url, this.cta_url)
    },
    onChooseYoutube(video) {
      this.raw_video = video
      this.vimeo = null
      this.cta_url = video.url
      this.alt = video.name
      let youTubeId = _.getYouTubeVideoId(this.cta_url)
      this.url = `https://img.youtube.com/vi/${youTubeId}/mqdefault.jpg`
      this.setTxt(video)
      this.btn_txt = trans('play_video')
      this.show_media = false
      this.generatePreview(this.url, this.cta_url)
    },
    onChooseVimeo(video) {
      this.raw_video = video
      this.vimeo = video
      this.cta_url = video.url
      this.alt = video.name
      this.url = video.preview_image_url
      this.setTxt(video)
      this.btn_txt = trans('play_video')
      let vimeoId = _.getVimeoVideoId(video.url)
      this.show_media = false
      this.busy = true
      let url = `/organization/${this.organization.id}/media/vimeo/${encodeURIComponent(vimeoId)}`
      axios.get(url)
        .then(response => {
          this.busy = false
          this.url = response.data.preview_image_url || response.data.thumbnail_url || this.url
          this.generatePreview(this.url, video.url)
          this.onVimeoChange()
        })
        .catch(error => {
          this.busy = false
          // Not found because bad URL or the video is private
          this.url = 'https://activitymessenger-assets-ca.s3.amazonaws.com/help/vimeo-placeholder.png'
          this.emitChanges()
          this.onVimeoChange()
        })
    },
    onVideoFileChange() {
      const url = `/organization/${this.organization.id}/media/video`
      this.busy = true
      this.error = null
      let formData = new FormData()
      formData.append("file", this.file)
      formData.append('clip', 0)
      formData.append('watermark', 0)
      axios.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: e => {
          this.upload_percent = Math.round((e.loaded * 100) / e.total)
        },
      })
        .then(response => {
          this.upload_percent = 100
          setTimeout(() => {
            this.busy = false
            this.onChooseVideo(response.data)
            this.upload_percent = null
          }, 4000)
        })
        .catch(this.handleError)
    },
    generatePreview(preview_url, video_url) {
      this.busy = true
      this.error = null
      let url = `/organization/${this.organization.id}/media/video_preview`
      axios.post(url, {
        preview_url: preview_url,
        video_url: video_url,
      })
        .then(response => {
          this.busy = false
          this.url = response.data.preview_url
          this.emitChanges()
        })
        .catch(this.handleError)
    },
    handleError(error) {
      this.busy = false
      this.error = _.errorFromAjax(error)
      this.$bvToast.toast(this.error, {
        title: trans('error'),
        variant: 'danger',
        solid: true,
      })
    },
  },
  mounted() {
    window.blockVideoTabComponent = this
  },
}
</script>
